<template>
	<Modal v-model:visible="visible" :title="$t('confirmImportModal.headers.' + mode)" :width="900" @ok="onOk" @cancel="onCancel">
		<Table :columns="columns" :data-source="tableRecords" :pagination="false" size="small">
			<template #bodyCell="{ column, record }">
				<template v-if="column.dataIndex === 'status'">
					<LabeledSwitch
						v-if="['confirm', 'add'].includes(record.status)"
						v-model="record.store"
						:small="true"
						:rightLabel="$t('confirmImportModal.statuses.' + record.status)"
					/>
					<template v-else-if="record.status === 'error'">
						<TypographyText type="danger">{{ $t('confirmImportModal.statuses.' + record.status) }}</TypographyText>
					</template>
					<template v-else>
						{{ $t('confirmImportModal.statuses.' + record.status) }}
					</template>
				</template>
				<template v-else-if="column.dataIndex === 'message'">
					<component v-if="record.component" :is="record.component" />
					<div v-if="record.status === 'confirm'">
						{{ $t('confirmImportModal.comments.' + (record.store ? 'overwrite' : 'pass')) }}
					</div>
					<div v-else-if="record.status === 'error'">
						<TypographyText type="danger">{{ $t('confirmImportModal.comments.pass') }}</TypographyText>
					</div>
					<div v-else-if="record.status === 'add'">
						{{ $t('confirmImportModal.comments.' + (record.store ? 'add' : 'pass')) }}
					</div>
				</template>
			</template>
		</Table>
		<LabeledSwitch
			v-if="hasToggleableRecords"
			class="pd-md-top"
			v-model="selectAllSwitchValue"
			:rightLabel="$t('confirmImportModal.selectAllSwitch')"
			@change="toggleSelectAll"
		/>
		<SubNetworksBlock
			v-if="mode === 'base_stations'"
			:formState="subNetworksFormState"
			:offset="0"
			:description="$t('confirmImportModal.subNetworksDescription')"
			@updateFormState="updateSubNetworksFormState"
		/>
		<template #footer>
			<Button key="back" @click="onCancel">{{ $t('confirmImportModal.cancelButtonLabel') }}</Button>
			<Button key="submit" type="primary" :loading="isSubmitting" :disabled="selectedItemsCount === 0" @click="onOk">
				{{ $t('confirmImportModal.okButtonLabels.' + mode, selectedItemsCount) }}
			</Button>
		</template>
	</Modal>
</template>

<script>
import { ref, reactive, toRaw, computed, watch } from 'vue';
import { useVModel } from '@vueuse/core'

import { Modal, Button, Table, TypographyText, message } from 'ant-design-vue';

import { useI18n } from 'vue-i18n';


import Api from '@/api/Api';
import Consts from '@/api/Consts';
import Utils from '@/api/Utils';

import { getErrorComponents, showErrorMessage } from '@/components/common/errorMessage';

import LabeledSwitch from '@/components/common/LabeledSwitch.vue';
import VerticalLayout from '@/components/common/VerticalLayout.vue';
import SubNetworksBlock from '@/components/addBaseStation/SubNetworksBlock.vue';


const getComponent = (item) => {
	if (!item.message) {
		return null;
	}
	const components = getErrorComponents(item.message);
	if (components.length === 0) {
		return null;
	}
	return components[0];
};

const prepareToImport = objects => toRaw(objects.filter(item => item.store).map(item => item.obj_data));


const TYPE_BASE_STATION = 'base_station';
const TYPE_VRS_NETWORK = 'vrs_network';
const TYPE_SUB_NETWORK = 'sub_network';
const TYPE_EXTERNAL_SOURCE = 'external_source';

const prepareData = overrides_func => item => ({
	...item,
	...overrides_func(item),
	store: item.status === 'add' || item.status === 'confirm',
	component: getComponent(item),
});


export default {
	components: {
		Modal, Button, Table, LabeledSwitch, TypographyText,
		SubNetworksBlock,
		VerticalLayout,
	},
	props: {
		modelValue: {
			type: Boolean,
			default: false,
		},
		mode: {
			type: String,
			required: true,
		},
		importData: {
			type: Object,
			required: true,
		},
		onSuccess: {
			type: Function,
			required: true,
		},
	},
	setup(props, { emit }) {
		const { t } = useI18n();
		const isSubmitting = ref(false);
		const tableRecords = ref([]);

		const visible = useVModel(props, 'modelValue', emit);
		const selectedItemsCount = computed(() => tableRecords.value.filter(item => item.store).length);

		const TOGGLEABLE_STATUSES = ['add', 'confirm'];
		const toggleableRecords = computed(() => tableRecords.value.filter(item => TOGGLEABLE_STATUSES.includes(item.status)));
		const hasToggleableRecords = computed(() => toggleableRecords.value.length > 0);
		const selectAllSwitchValue = ref(true);
		const toggleSelectAll = () => {
			toggleableRecords.value.forEach(record => {
				record.store = selectAllSwitchValue.value;
			});
		};

		const subNetworksFormState = reactive({ subNetworks: [] });
		const updateSubNetworksFormState = (field, value) => {
			subNetworksFormState[field] = value;
		};
		watch(visible, value => {
			if (value) {
				subNetworksFormState.subNetworks = [];
				selectAllSwitchValue.value = true;
			}
		});

		const baseStationsOverrides = item => ({
			type: TYPE_BASE_STATION,
			label: item.obj_data.label,
			mntpntInt: item.obj_data.mntpnt_int,
		});

		const vrsNetworksOverrides = () => ({
			type: TYPE_VRS_NETWORK,
			label: t('confirmImportModal.labels.globalSettings'),
			mntpntInt: t('confirmImportModal.labels.globalSettings'),
		});

		const subNetworksOverrides = item => ({
			type: TYPE_SUB_NETWORK,
			label: t('confirmImportModal.labels.subnetwork') + item.obj_data.title,
			mntpntInt: item.obj_data.label,
		});

		const externalSourcesOverrides = item => ({
			type: TYPE_EXTERNAL_SOURCE,
			label: t('confirmImportModal.labels.externalSource') + item.obj_data.mntpnt_src.toUpperCase(),
			mntpntInt: item.obj_data.mntpnt_src.toUpperCase(),
		});

		watch(() => props.importData, value => {
			const localBaseStations = (value.base_stations != null ? value.base_stations : []).map(prepareData(baseStationsOverrides));
			const localVrsNetworks = (value.vrs_networks != null ? value.vrs_networks : []).map(prepareData(vrsNetworksOverrides));
			const localSubNetworks = (value.sub_networks != null ? value.sub_networks : []).map(prepareData(subNetworksOverrides));
			const localExternalSources = (value.external_sources != null ? value.external_sources : []).map(prepareData(externalSourcesOverrides));
			tableRecords.value = [
				...Utils.sortArray(localBaseStations, Utils.objectFieldMultiSorter('status')),
				...Utils.sortArray(localVrsNetworks, Utils.objectFieldMultiSorter('status')),
				...Utils.sortArray(localSubNetworks, Utils.objectFieldMultiSorter('status')),
				...Utils.sortArray(localExternalSources, Utils.objectFieldMultiSorter('status')),
			];
		}, { immediate: true });

		const onOk = () => {
			isSubmitting.value = true;
			const toImport = {
				base_stations: prepareToImport(tableRecords.value.filter(item => item.type === TYPE_BASE_STATION)),
				vrs_networks: prepareToImport(tableRecords.value.filter(item => item.type === TYPE_VRS_NETWORK)),
				sub_networks: prepareToImport(tableRecords.value.filter(item => item.type === TYPE_SUB_NETWORK)),
				external_sources: prepareToImport(tableRecords.value.filter(item => item.type === TYPE_EXTERNAL_SOURCE)),
				sub_network_ids: props.mode === 'base_stations' ? toRaw(subNetworksFormState.subNetworks) : [],
			};
			Api.confBridgeUploadJSONFinish(toImport, props.mode, response => {
				if (response.status === 'ok') {
					message.success(t('confirmImportModal.messages.success'));
					props.onSuccess();
				} else {
					message.error(t('confirmImportModal.messages.error'))
					showErrorMessage(response.errors);
				}
				isSubmitting.value = false;
			});
			visible.value = false;
		};

		const onCancel = () => {
			visible.value = false;
		};

		return {
			isSubmitting,
			tableRecords,
			visible,
			selectedItemsCount,
			hasToggleableRecords,
			selectAllSwitchValue,
			toggleSelectAll,
			subNetworksFormState,
			updateSubNetworksFormState,
			onOk,
			onCancel,
			columns: Consts.baseStationsImportTableColumnsComp,
		};
	},
};
</script>
